// CircuitTab — the Circuito FIP season calendar: four away events per league
// season (Bronce → Platino), entry with travel cost, per-player ranking
// points and the season's results log.

import { useGameStore } from '@/store/gameStore'
import {
  CIRCUIT_TIERS, getTravelCost, type CircuitCalendarSlot, type CircuitTier,
} from '@/config/circuitConfig'
import { REGION_META } from '@/config/regionConfig'
import { canEnterCircuitEvent, enterCircuitEvent } from '@/game/systems/CircuitSystem'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { playerPortraitSrc } from '../helpers/portraits'
import { SectionDeck, EmptyStateRich } from '../components/CourtVision'
import { AngledButton } from '../components/broadcast'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import {
  Globe, Medal, Trophy, Lock, Plane, CheckCircle2, XCircle, CalendarDays, Crown, Star,
  type LucideIcon,
} from 'lucide-react'

const TIER_ICONS: Record<CircuitTier, LucideIcon> = {
  fip_bronze: Medal,
  fip_silver: Medal,
  fip_gold: Trophy,
  fip_platinum: Trophy,
  p2: Crown,
  p1: Crown,
  major: Crown,
  finals: Star,
}

const TIER_COLORS: Record<CircuitTier, string> = {
  fip_bronze: '#B87333',
  fip_silver: '#94A3B8',
  fip_gold: 'var(--gold)',
  fip_platinum: '#38BDF8',
  p2: '#A78BFA',
  p1: '#8B5CF6',
  major: '#F5C542',
  finals: '#FB7185',
}

function resultLabel(slot: CircuitCalendarSlot): string {
  const r = slot.result!
  if (r.champion) return `🏆 Campeones · +${r.points} pts · ${r.prize.toLocaleString('es-ES')}€`
  if (r.roundsWon === 0 && r.points === 0 && !slot.pairIds) return 'No disputado'
  const stage = r.roundsWon === r.totalRounds - 1 ? 'final'
    : r.roundsWon === r.totalRounds - 2 ? 'semifinales'
    : `ronda ${r.roundsWon + 1}`
  return `Eliminados en ${stage} · +${r.points} pts`
}

function SlotRow({ slot }: { slot: CircuitCalendarSlot }) {
  const { week, region, money } = useGameStore()
  const def = CIRCUIT_TIERS[slot.tier]
  const Icon = TIER_ICONS[slot.tier]
  const check = canEnterCircuitEvent(slot)
  const travelCost = getTravelCost(slot, region)
  const missed = !slot.result && !slot.entered && week >= slot.week
  const weeksLeft = slot.week - week

  const handleEnter = () => {
    const ok = enterCircuitEvent(slot.id)
    EventBus.emit(GameEvents.NOTIFY, ok
      ? { message: `Inscritos en el ${slot.name} · se disputa en ${weeksLeft} semana${weeksLeft === 1 ? '' : 's'}`, type: 'success' }
      : { message: 'No se pudo completar la inscripción', type: 'error' })
  }

  return (
    <div className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem', opacity: missed ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span className="icon-tile" style={{ color: TIER_COLORS[slot.tier] }}>
          <Icon size={18} />
        </span>
        <div className="row-tile-main">
          <div className="row-tile-title">
            {slot.name}
            <span className="pill" style={{ background: 'var(--gold-fill)', color: TIER_COLORS[slot.tier] }}>
              {def.championPoints} pts
            </span>
          </div>
          <div className="row-tile-sub">
            Semana {slot.week} · {REGION_META[slot.hostRegion].label}
            {travelCost > 0
              ? <> · viaje {travelCost.toLocaleString('es-ES')}€</>
              : ' · en casa (sin viaje)'}
            {' · '}premio {def.prizeChampion.toLocaleString('es-ES')}€
          </div>
        </div>
        <div className="row-tile-side">
          {slot.result ? (
            <span className={`pill ${slot.result.champion ? 'pill-success' : 'pill-info'}`}>
              {slot.result.champion ? <Trophy size={10} /> : <CheckCircle2 size={10} />} Disputado
            </span>
          ) : slot.entered ? (
            <span className="pill pill-success">
              <Plane size={10} /> Inscritos · {weeksLeft <= 0 ? 'esta semana' : `en ${weeksLeft} sem.`}
            </span>
          ) : missed ? (
            <span className="pill pill-info"><XCircle size={10} /> No disputado</span>
          ) : check.can ? (
            <AngledButton tone="gold" onClick={handleEnter} disabled={money < travelCost}>
              Inscribirse{travelCost > 0 ? ` · ${travelCost.toLocaleString('es-ES')}€` : ''}
            </AngledButton>
          ) : (
            <span className="pill pill-warning" title={check.reasons.join(' · ')}>
              <Lock size={10} /> {check.reasons[0]}
            </span>
          )}
        </div>
      </div>

      {slot.result && (
        <div className="meta-text" style={{ paddingLeft: '2.4rem' }}>
          {resultLabel(slot)}
        </div>
      )}
    </div>
  )
}

export function CircuitTabContent() {
  const { circuit, league, players } = useGameStore()

  if (!league) {
    return (
      <EmptyStateRich
        icon={Globe}
        title="El circuito sigue el calendario de la liga"
        sub="Inscríbete primero en la liga de clubes: cada temporada trae cuatro pruebas FIP a las que viajar"
      />
    )
  }

  if (!circuit || circuit.season !== league.season) {
    return (
      <EmptyStateRich
        icon={CalendarDays}
        title="Calendario en preparación"
        sub="La Federación publicará las pruebas de esta temporada en los próximos días"
      />
    )
  }

  const ranked = Object.entries(circuit.ranking)
    .map(([playerId, points]) => ({ player: players.find(p => p.id === playerId), points }))
    .filter((r): r is { player: NonNullable<typeof r.player>; points: number } => Boolean(r.player) && r.points > 0)
    .sort((a, b) => b.points - a.points)

  return (
    <div>
      <SectionDeck
        title="Calendario Circuito FIP"
        icon={CalendarDays}
        meta={`Temporada ${circuit.season}`}
      >
        <div className="hint-box" style={{ marginBottom: '0.6rem' }}>
          Tu mejor pareja viaja a las pruebas del circuito. FIP Bronce/Plata abiertas a cualquier
          club federado; <strong>Oro y Platino exigen 3ª División</strong>; los{' '}
          <strong>Premier Padel (P2 → 2ª, P1 y Major → 1ª)</strong> son la élite, y el{' '}
          <strong>Máster Final solo invita a parejas con puntos suficientes</strong> de la temporada.
        </div>
        <div className="space-y-2">
          {circuit.calendar.map(slot => <SlotRow key={slot.id} slot={slot} />)}
        </div>
      </SectionDeck>

      <SectionDeck title="Ranking FIP del club" icon={Medal} meta={`${ranked.length} jugadores con puntos`}>
        {ranked.length === 0 ? (
          <EmptyStateRich
            icon={Medal}
            title="Sin puntos todavía"
            sub="Inscríbete en una prueba: cada ronda superada suma puntos al ranking de tus jugadores"
          />
        ) : (
          <div className="space-y-2">
            {ranked.map(({ player, points }, i) => (
              <div key={player.id} className="row-tile">
                <span style={{ width: 22, textAlign: 'center', fontWeight: 800, color: i === 0 ? 'var(--gold)' : 'var(--text-secondary)' }}>
                  {i + 1}
                </span>
                <PlayerAvatar id={player.id} name={player.name} size={30} src={playerPortraitSrc(player)} />
                <div className="row-tile-main">
                  <div className="row-tile-title">{player.name}</div>
                  <div className="row-tile-sub">Nivel {player.level}</div>
                </div>
                <span style={{ fontWeight: 800, color: 'var(--gold)' }}>{points} pts</span>
              </div>
            ))}
          </div>
        )}
      </SectionDeck>
    </div>
  )
}
